import type { ComponentProps } from 'react';
import type { frontendQueryCachePersistence } from '@gonfalon/dogfood-flags/dogfood-flags';
import { assertNever } from '@gonfalon/types/assertNever';
import type { QueryOptions } from '@tanstack/react-query';
import type { Persister, PersistQueryClientProvider } from '@tanstack/react-query-persist-client';

import { createCacheBuster } from './createCacheBuster';
import { createCacheKey } from './createCacheKey';
import { createIndexedDBPersister } from './createIndexedDBPersister';
import { createLocalStoragePersister } from './createLocalStoragePersister';

export interface PersisterConfigurationOptions extends Pick<QueryOptions, 'gcTime'> {
  strategy: ReturnType<typeof frontendQueryCachePersistence>;
}

export function createPersistenceConfiguration({ gcTime, strategy }: PersisterConfigurationOptions) {
  if (strategy === 'disabled') {
    return undefined;
  }

  const buster = createCacheBuster();
  const cacheKey = createCacheKey();

  let persister: Persister;
  switch (strategy) {
    case 'localstorage':
      persister = createLocalStoragePersister({ cacheKey });
      break;
    case 'indexeddb':
      persister = createIndexedDBPersister({ cacheKey, buster });
      break;
    default:
      assertNever(strategy);
      break;
  }

  return {
    persister,
    maxAge: gcTime,
    buster,
    dehydrateOptions: {
      shouldDehydrateQuery: (query) => {
        if (
          query.state.status === 'success' &&
          (query.queryKey.includes('migration-metrics') ||
            query.queryKey.includes('shortcuts') ||
            query.queryKey.includes('contexts'))
        ) {
          return true;
        }

        return !!query.meta?.persist;
      },
    },
  } satisfies ComponentProps<typeof PersistQueryClientProvider>['persistOptions'];
}
